import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  CircularProgress,
  Divider
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import GetAppIcon from '@mui/icons-material/GetApp'
import DateRangeIcon from '@mui/icons-material/DateRange'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import styles from './ExportDialog.module.scss'

const FIXED_COLUMNS = [
  'QA Date',
  'Call ID',
  'Campaign',
  'Agent',
  'Disposition',
  'QA Grade',
  'Overall Score'
]

const CRITERIA_COLUMNS = [
  { key: 'callerNameConfirmation', label: 'Caller Name Confirmation' },
  { key: 'companyNameConfirmation', label: 'Company Name Confirmation' },
  { key: 'emailPhonetics', label: 'Email Verification' },
  { key: 'phoneNumberVerification', label: 'Phone Number Verification' },
  { key: 'opposingPartyPhonetics', label: 'Opposing Party Phonetics' },
  { key: 'callNotes5Ws', label: 'Call Notes (5Ws)' }
]

const ExportDialog = ({
  open,
  onClose,
  onExport,
  currentFilters,
  totalResults = 0,
  loading = false
}) => {
  const [dateRangeOption, setDateRangeOption] = useState('current')
  const [customStartDate, setCustomStartDate] = useState(currentFilters.startDate || '')
  const [customEndDate, setCustomEndDate] = useState(currentFilters.endDate || '')

  const [selectedCriteria, setSelectedCriteria] = useState(
    CRITERIA_COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
  )

  const selectedCriteriaCount = Object.values(selectedCriteria).filter(Boolean).length
  const totalColumns = FIXED_COLUMNS.length + selectedCriteriaCount

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return null
    const date = new Date(dateStr + 'T00:00:00')
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const currentDateRangeText = useMemo(() => {
    if (currentFilters.startDate && currentFilters.endDate) {
      return `${formatDateForDisplay(currentFilters.startDate)} - ${formatDateForDisplay(currentFilters.endDate)}`
    } else if (currentFilters.startDate) {
      return `From ${formatDateForDisplay(currentFilters.startDate)}`
    } else if (currentFilters.endDate) {
      return `Until ${formatDateForDisplay(currentFilters.endDate)}`
    }
    return 'All dates'
  }, [currentFilters.startDate, currentFilters.endDate])

  const handleCriteriaToggle = (key) => {
    setSelectedCriteria(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const handleSelectAll = () => {
    setSelectedCriteria(
      CRITERIA_COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
    )
  }

  const handleDeselectAll = () => {
    setSelectedCriteria(
      CRITERIA_COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: false }), {})
    )
  }

  const handleExport = async () => {
    const exportOptions = {
      dateRangeOption,
      customStartDate: dateRangeOption === 'custom' ? customStartDate : null,
      customEndDate: dateRangeOption === 'custom' ? customEndDate : null,
      selectedCriteria
    }
    await onExport(exportOptions)
  }

  React.useEffect(() => {
    if (open) {
      setDateRangeOption('current')
      setCustomStartDate(currentFilters.startDate || '')
      setCustomEndDate(currentFilters.endDate || '')
      setSelectedCriteria(
        CRITERIA_COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
      )
    }
  }, [open, currentFilters.startDate, currentFilters.endDate])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={styles.dialog}
      aria-labelledby="export-dialog-title"
    >
      {/* Header */}
      <DialogTitle disableTypography className={styles.dialogTitle}>
        <div className={styles.titleContent}>
          <GetAppIcon className={styles.titleIcon} />
          <Typography className={styles.titleText}>Export QA Results</Typography>
        </div>
        <IconButton
          className={styles.closeButton}
          onClick={onClose}
          aria-label="close"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent className={styles.dialogContent}>
        {/* Date Range Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <DateRangeIcon className={styles.sectionIcon} />
            <Typography className={styles.sectionTitle}>Date Range</Typography>
          </div>
          <div className={styles.sectionCard}>
            <RadioGroup
              value={dateRangeOption}
              onChange={(e) => setDateRangeOption(e.target.value)}
              className={styles.radioGroup}
            >
              <FormControlLabel
                value="current"
                control={<Radio className={styles.radio} size="small" />}
                label={`Use current filters (${currentDateRangeText})`}
                className={styles.radioLabel}
              />
              <FormControlLabel
                value="custom"
                control={<Radio className={styles.radio} size="small" />}
                label="Custom date range"
                className={styles.radioLabel}
              />
            </RadioGroup>

            {dateRangeOption === 'custom' && (
              <div className={styles.dateInputsRow}>
                <div className={styles.dateInputGroup}>
                  <label className={styles.dateLabel}>Start Date</label>
                  <input
                    type="date"
                    className={styles.dateInput}
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                  />
                </div>
                <div className={styles.dateInputGroup}>
                  <label className={styles.dateLabel}>End Date</label>
                  <input
                    type="date"
                    className={styles.dateInput}
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Columns Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <ViewColumnIcon className={styles.sectionIcon} />
            <Typography className={styles.sectionTitle}>Columns to Export</Typography>
          </div>
          <div className={styles.sectionCard}>
            {/* Fixed Columns */}
            <Typography className={styles.fixedColumnsLabel}>
              Fixed Columns (always included):
            </Typography>
            <Typography className={styles.fixedColumnsText}>
              {FIXED_COLUMNS.join(', ')}
            </Typography>

            <Divider style={{ margin: '12px 0' }} />

            {/* QA Criteria */}
            <div className={styles.criteriaHeader}>
              <Typography className={styles.criteriaLabel}>QA Criteria:</Typography>
              <div className={styles.selectButtons}>
                <Button
                  className={styles.selectButton}
                  onClick={handleSelectAll}
                  size="small"
                >
                  Select All
                </Button>
                <Button
                  className={styles.selectButton}
                  onClick={handleDeselectAll}
                  size="small"
                >
                  Deselect All
                </Button>
              </div>
            </div>

            <div className={styles.checkboxGrid}>
              {CRITERIA_COLUMNS.map((col) => (
                <FormControlLabel
                  key={col.key}
                  control={
                    <Checkbox
                      checked={selectedCriteria[col.key]}
                      onChange={() => handleCriteriaToggle(col.key)}
                      className={styles.checkbox}
                      size="small"
                    />
                  }
                  label={col.label}
                  className={styles.checkboxLabel}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Preview Section */}
        <Box className={styles.previewSection}>
          <Typography className={styles.previewText}>
            Exporting <span>{totalColumns} columns</span>
            {dateRangeOption === 'current' && totalResults > 0 && (
              <> for <span>{totalResults.toLocaleString()} results</span></>
            )}
            {dateRangeOption === 'custom' && (
              <> for custom date range</>
            )}
          </Typography>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions className={styles.dialogActions}>
        <Button
          onClick={onClose}
          className={styles.cancelButton}
        >
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          className={styles.exportButton}
          disabled={loading}
          startIcon={loading ? (
            <CircularProgress size={16} className={styles.buttonProgress} />
          ) : (
            <GetAppIcon />
          )}
        >
          {loading ? 'Exporting...' : 'Export CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

ExportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  currentFilters: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }).isRequired,
  totalResults: PropTypes.number,
  loading: PropTypes.bool
}

export default ExportDialog
